import {
  useEffect,
  useState,
} from "react";

import {
  head,
} from "../components/common/PageHeader.jsx";

import {
  btn,
} from "../components/forms/FormControls.jsx";

import {
  nodes,
} from "../components/common/reactHelpers.jsx";

import {
  state,
} from "../state/workspaceStore.js";

import {
  save,
} from "../utils/domain/model.js";

import {
  badge,
} from "../components/common/StatusBadge.jsx";

import {
  esc,
} from "../utils/format.js";

import {
  tableViews,
} from "../components/tables/RecordTable.jsx";


/* =========================================================
   API
========================================================= */

const COLLECTION_API =
  "https://api.praachinorganics.com/api/Collections";


/* =========================================================
   DELETE ICON
========================================================= */

function deleteIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7"></path>
    </svg>
  );
}


/* =========================================================
   STATUS
========================================================= */

function statusFromApi(
  status,
  statusName,
) {
  if (
    String(
      statusName || "",
    ).toLowerCase() ===
    "active"
  ) {
    return true;
  }

  if (
    String(
      statusName || "",
    ).toLowerCase() ===
    "inactive"
  ) {
    return false;
  }

  return Number(
    status,
  ) === 1;
}


/* =========================================================
   MAP API COLLECTION
========================================================= */

function mapCollection(
  item,
) {
  return {
    id:
      String(
        item?.id ??
        "",
      ),

    name:
      item?.name ||
      "",

    code:
      item?.code ||
      "",

    description:
      item?.description ||
      "",

    order:
      Number(
        item?.displayOrder ??
        1,
      ),

    launch:
      statusFromApi(
        item?.status,
        item?.statusName,
      ),

    archived:
      false,

    productCount:
      Number(
        item?.productCount ??
        0,
      ),

    orderCount:
      Number(
        item?.orderCount ??
        0,
      ),
  };
}


/* =========================================================
   POPUP
========================================================= */

function CollectionPopup({
  popup,
  deleting,
  onClose,
  onConfirm,
}) {
  if (
    !popup
  ) {
    return null;
  }

  const isConfirm =
    popup.type ===
    "confirm";

  const isSuccess =
    popup.type ===
    "success";

  const isError =
    popup.type ===
    "error";

  return (
    <div
      className="collection-popup-backdrop"
      role="presentation"
    >
      <div
        className="collection-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="collection-popup-title"
      >
        <div
          className={
            `collection-popup-icon ${isSuccess
              ? "success"
              : isError
                ? "error"
                : "warning"
            }`
          }
        >
          {
            isSuccess
              ? "✓"
              : isError
                ? "!"
                : "?"
          }
        </div>

        <h2
          id="collection-popup-title"
        >
          {popup.title}
        </h2>

        <p>
          {popup.message}
        </p>

        <div
          className="collection-popup-actions"
        >
          {
            isConfirm ? (
              <>
                <button
                  type="button"
                  className="btn secondary"
                  disabled={
                    deleting
                  }
                  onClick={
                    onClose
                  }
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn"
                  disabled={
                    deleting
                  }
                  onClick={
                    onConfirm
                  }
                >
                  {
                    deleting
                      ? "Deleting..."
                      : "Delete"
                  }
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={
                  onClose
                }
              >
                OK
              </button>
            )
          }
        </div>
      </div>
    </div>
  );
}


/* =========================================================
   COLLECTIONS UI
========================================================= */

export function collections(
  onDeleteCollection,
  deletingId,
) {
  return (
    <>
      {head(
        "ORGANISE",

        "Collections",

        "Manage display order, visibility and product membership.",

        btn(
          "＋ Add New Collection",
          'data-nav="collection-edit"',
        ),
      )}

      <>
        <div
          className="collection-grid"
        >
          {nodes(
            state.db.collections
              .filter(
                (c) =>
                  !c.archived,
              )

              .sort(
                (a, b) =>
                  a.order -
                  b.order,
              )

              .map(
                (c) => (
                  <section
                    key={
                      c.id
                    }
                    className="card collection"
                  >
                    <div
                      className="collection-top"
                    >
                      {badge(
                        c.launch
                          ? "Active"
                          : "Inactive",
                      )}
                    </div>

                    <h2>
                      {esc(
                        c.name,
                      )}
                    </h2>

                    <p>
                      {esc(
                        c.description,
                      )}
                    </p>

                    <div
                      className="collection-foot"
                    >
                      <span>
                        {
                          c.productCount ??
                          0
                        }

                        {" products"}
                      </span>

                      <span>
                        {"Order "}

                        {c.order}
                      </span>
                    </div>

                    <div
                      className="collection-card-actions"
                    >
                      <div
                        className="collection-manage-action"
                      >
                        {btn(
                          "Manage collection",

                          `data-nav="collection" data-id="${c.id}"`,

                          true,
                        )}
                      </div>

                      <button
                        type="button"
                        className="table-action"
                        title="Delete collection"
                        aria-label="Delete collection"
                        disabled={
                          String(
                            deletingId,
                          ) ===
                          String(
                            c.id,
                          )
                        }
                        onClick={
                          () =>
                            onDeleteCollection(
                              c,
                            )
                        }
                      >
                        {deleteIcon()}
                      </button>
                    </div>
                  </section>
                ),
              ),
          )}
        </div>

        {
          state.db.collections.some(
            (c) =>
              c.archived,
          ) ? (
            <section
              className="card space-top"
            >
              <h2>
                Archived collections
              </h2>

              {nodes(
                state.db.collections
                  .filter(
                    (c) =>
                      c.archived,
                  )

                  .map(
                    (c) => (
                      <button
                        key={
                          c.id
                        }
                        className="attention-row"
                        data-restore-collection={
                          c.id
                        }
                      >
                        {esc(
                          c.name,
                        )}

                        <span>
                          {"Restore →"}
                        </span>
                      </button>
                    ),
                  ),
              )}
            </section>
          ) : (
            ""
          )
        }
      </>
    </>
  );
}


/* =========================================================
   COLLECTIONS PAGE
========================================================= */

export default function CollectionsPage({
  recordId,
} = {}) {
  const [
    ,
    setRevision,
  ] =
    useState(0);

  const [
    deletingId,
    setDeletingId,
  ] =
    useState(null);

  const [
    pendingDelete,
    setPendingDelete,
  ] =
    useState(null);

  const [
    popup,
    setPopup,
  ] =
    useState(null);


  /* =======================================================
     GET ALL COLLECTIONS
     GET /api/Collections
  ======================================================= */

  useEffect(() => {
    let alive =
      true;

    async function loadCollections() {
      try {
        const response =
          await fetch(
            COLLECTION_API,
            {
              method:
                "GET",

              headers: {
                Accept:
                  "application/json",
              },
            },
          );

        let data =
          null;

        try {
          data =
            await response.json();

        } catch {
          data =
            null;
        }

        if (
          !response.ok
        ) {
          throw new Error(
            data?.message ||
            data?.title ||
            `Unable to load collections (${response.status}).`,
          );
        }

        if (
          !alive
        ) {
          return;
        }

        const mappedCollections =
          (
            Array.isArray(
              data,
            )
              ? data
              : []
          ).map(
            mapCollection,
          );

        state.db.collections =
          mappedCollections;

        save(
          localStorage,
          state.db,
        );

        setRevision(
          (n) =>
            n + 1,
        );

      } catch (error) {
        console.error(
          "COLLECTIONS GET ERROR:",
          error,
        );

        if (
          alive
        ) {
          setPopup({
            type:
              "error",

            title:
              "Unable to load collections",

            message:
              error?.message ||
              "Something went wrong while loading collections.",
          });
        }
      }
    }

    loadCollections();

    return () => {
      alive =
        false;
    };

  }, []);


  /* =======================================================
     OPEN DELETE POPUP
  ======================================================= */

  function handleDeleteCollection(
    collection,
  ) {
    const collectionId =
      Number(
        collection?.id,
      );

    if (
      !Number.isInteger(
        collectionId,
      ) ||
      collectionId <= 0
    ) {
      setPopup({
        type:
          "error",

        title:
          "Invalid collection",

        message:
          "A valid collection ID is required.",
      });

      return;
    }

    setPendingDelete(
      collection,
    );

    setPopup({
      type:
        "confirm",

      title:
        "Delete collection?",

      message:
        `Are you sure you want to delete "${collection.name}"?`,
    });
  }


  /* =======================================================
     CONFIRM DELETE
     DELETE /api/Collections/{id}
  ======================================================= */

  async function confirmDelete() {
    if (
      !pendingDelete
    ) {
      return;
    }

    const collectionId =
      Number(
        pendingDelete.id,
      );

    try {
      setDeletingId(
        collectionId,
      );

      const response =
        await fetch(
          `${COLLECTION_API}/${collectionId}`,
          {
            method:
              "DELETE",

            headers: {
              Accept:
                "application/json",
            },
          },
        );

      let data =
        null;

      if (
        response.status !==
        204
      ) {
        try {
          data =
            await response.json();

        } catch {
          data =
            null;
        }
      }

      if (
        !response.ok
      ) {
        throw new Error(
          data?.message ||
          data?.title ||
          `Unable to delete collection (${response.status}).`,
        );
      }

      const deletedName =
        pendingDelete.name;

      state.db.collections =
        state.db.collections.filter(
          (item) =>
            String(
              item.id,
            ) !==
            String(
              collectionId,
            ),
        );

      save(
        localStorage,
        state.db,
      );

      setRevision(
        (n) =>
          n + 1,
      );

      setPendingDelete(
        null,
      );

      setPopup({
        type:
          "success",

        title:
          "Collection deleted",

        message:
          `"${deletedName}" was deleted successfully.`,
      });

    } catch (error) {
      console.error(
        "COLLECTION DELETE ERROR:",
        error,
      );

      setPopup({
        type:
          "error",

        title:
          "Delete failed",

        message:
          error?.message ||
          "Unable to delete the collection.",
      });

    } finally {
      setDeletingId(
        null,
      );
    }
  }


  function closePopup() {
    if (
      deletingId
    ) {
      return;
    }

    setPopup(
      null,
    );

    setPendingDelete(
      null,
    );
  }


  return (
    <>
      <style>
        {`
          .collection-card-actions{
            display:flex;
            align-items:center;
            gap:8px;
            width:100%;
          }

          .collection-manage-action{
            flex:1;
            min-width:0;
          }

          .collection-manage-action .btn{
            width:100%;
          }

          .collection-card-actions .table-action{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            width:40px;
            height:40px;
            min-width:40px;
            padding:0;
            flex:none;
            border:1px solid #dae5da;
            border-radius:7px;
            background:#f7faf6;
            color:#2d6946;
          }

          .collection-card-actions .table-action svg{
            width:17px;
            height:17px;
          }

          .collection-card-actions .table-action:hover{
            background:#e5f1e5;
          }

          .collection-card-actions .table-action:disabled{
            opacity:.5;
            cursor:default;
          }

          .collection-popup-backdrop{
            position:fixed;
            inset:0;
            z-index:9999;
            display:flex;
            align-items:center;
            justify-content:center;
            padding:20px;
            background:#183e2c80;
          }

          .collection-popup{
            width:min(460px,100%);
            max-height:calc(100vh - 40px);
            overflow:auto;
            background:#fff;
            border:1px solid #d8e5d6;
            border-radius:14px;
            box-shadow:0 30px 100px #122e2b50;
            padding:28px;
            text-align:center;
            color:#2d5138;
          }

          .collection-popup-icon{
            width:52px;
            height:52px;
            margin:0 auto 18px;
            border-radius:50%;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:24px;
            font-weight:800;
          }

          .collection-popup-icon.warning{
            background:#fff3dc;
            color:#976d2e;
          }

          .collection-popup-icon.success{
            background:#e5f4e6;
            color:#347144;
          }

          .collection-popup-icon.error{
            background:#fbeae7;
            color:#ab5143;
          }

          .collection-popup h2{
            margin:0 0 10px;
          }

          .collection-popup p{
            margin:0;
            color:#6d8872;
            font-size:13px;
            line-height:1.6;
            overflow-wrap:anywhere;
          }

          .collection-popup-actions{
            display:flex;
            justify-content:flex-end;
            gap:10px;
            margin-top:24px;
          }

          @media(max-width:650px){
            .collection-popup-backdrop{
              padding:14px;
              align-items:flex-end;
            }

            .collection-popup{
              width:100%;
              padding:22px 18px;
              border-radius:14px;
            }

            .collection-popup-actions{
              flex-direction:column-reverse;
            }

            .collection-popup-actions .btn{
              width:100%;
            }

            .collection-card-actions{
              width:100%;
            }

            .collection-card-actions .table-action{
              width:40px;
              height:40px;
              min-width:40px;
            }
          }
        `}
      </style>

      {tableViews(
        collections(
          handleDeleteCollection,
          deletingId,
        ),
      )}

      <CollectionPopup
        popup={
          popup
        }
        deleting={
          Boolean(
            deletingId,
          )
        }
        onClose={
          closePopup
        }
        onConfirm={
          confirmDelete
        }
      />
    </>
  );
}
